import { observer } from "mobx-react-lite";
import { AlertCircle } from "lucide-react";
import { AppLink } from "@/components/AppLink";
import { GroupBadgeList } from "@/components/badges/GroupBadge";
import { structureType } from "@/config/structureType";
import { networkTypeConfig } from "@/config/networkType";
import { cn } from "@/lib/utils";
import { StructureConfigBox } from "@/components/StructureConfigBox";

interface StructurePopUpProps {
	structure: Dryp.Structure;
	missingConnectivity?: boolean;
	// Optional filter integration. selectedTypeIds / selectedGroupIds drive
	// the badge's selected styling. onToggleType / onToggleGroup make the
	// badges clickable; omit to render them as static labels.
	selectedTypeIds?: ReadonlySet<string>;
	selectedGroupIds?: ReadonlySet<string>;
	onToggleType?: (typeId: Dryp.StructureTypeId) => void;
	onToggleGroup?: (groupId: Dryp.StructureGroupId) => void;
}

export const StructurePopUp: React.FC<StructurePopUpProps> = observer(
	({ structure, missingConnectivity, selectedTypeIds, selectedGroupIds, onToggleType, onToggleGroup }) => {
		const typeConfig = structureType[structure.type.id];
		const networkConfig = structure.networkType ? networkTypeConfig[structure.networkType] : null;
		const isSelectedType = selectedTypeIds?.has(String(structure.type.id)) ?? false;
		const groupSelections = selectedGroupIds ?? new Set<string>();

		return (
			<div className="px-2.5 py-2 space-y-2">
				<div className="flex items-start justify-between gap-2">
					<AppLink
						to="Dashboard.Structures.Structure"
						params={{ id: structure.id }}
						className="text-sm font-semibold text-foreground leading-tight hover:underline"
					>
						{structure.name}
					</AppLink>
					<div className="flex items-center gap-1 flex-shrink-0">
						<div
							className={cn(
								"rounded-full px-1.5 h-4 inline-flex items-center text-[9px] font-medium transition-all",
								onToggleType && "cursor-pointer hover:contrast-150",
							)}
							onClick={onToggleType ? () => onToggleType(structure.type.id) : undefined}
							style={{
								backgroundColor: isSelectedType ? typeConfig?.bgColor : "transparent",
								color: isSelectedType ? typeConfig?.textColor : typeConfig?.bgColor,
								border: `1px solid ${typeConfig?.bgColor}`,
							}}
						>
							{structure.type.name}
						</div>
						{networkConfig && (
							<div
								className="rounded-full px-1.5 h-4 inline-flex items-center text-[9px] font-medium"
								style={{
									backgroundColor: networkConfig.bgColor + "18",
									color: networkConfig.bgColor,
								}}
							>
								{networkConfig.label}
							</div>
						)}
					</div>
				</div>

				{missingConnectivity && (
					<div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700">
						<AlertCircle className="mt-px h-3.5 w-3.5 shrink-0 text-amber-500" />
						<span>
							<span className="font-medium">Missing connectivity.</span> Right-click the marker to add a
							connection.
						</span>
					</div>
				)}

				{structure.description && (
					<p className="text-xs text-muted-foreground leading-relaxed">{structure.description}</p>
				)}

				{structure.groups.length > 0 && (
					<div className="pt-1.5 border-t">
						<GroupBadgeList
							groups={structure.groups}
							selectedIds={groupSelections as Set<Dryp.StructureGroupId>}
							onBadgeClick={onToggleGroup ?? (() => {})}
						/>
					</div>
				)}
				<StructureConfigBox structureId={structure.id} />
			</div>
		);
	},
);
